import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import ClearIcon from '@mui/icons-material/Clear';
import {
  Box,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';

import { AccordionItemPanel } from 'src/components/common/Accordion';
import AccordionItem from 'src/components/common/Accordion/components/AccordionItem';
import AccordionItemHeader from 'src/components/common/Accordion/components/AccordionItemHeader';
import { CREATE_FOLLOW_UP_MODAL_ACCORDION_NAMES } from 'src/components/myFollowUp/MyFollowUpPage/CreateMyFollowUpModal/constants';
import { useCreateActionItemContext } from 'src/components/myFollowUp/MyFollowUpPage/CreateMyFollowUpModal/CreateActionItemContext';

import { POTENTIAL_FOLLOW_UP_OWNERS_PAGINATION_LIMIT } from './usePotentialFollowUpOwners';
import { useSelectOwner } from './useSelectOwner';

const SelectOwner: FC = () => {
  const { t } = useTranslation('myFollowUp');
  const { followUpOwner, surveyId, selectedOrgUnits } =
    useCreateActionItemContext();

  const {
    followUpOwners,
    handleOwnerSelect,
    selectedOwnerName,
    handleSearchChange,
    handleClearSearch,
    searchString,
    isLoading,
    error,
    isFetching,
  } = useSelectOwner();

  return (
    <AccordionItem itemName={CREATE_FOLLOW_UP_MODAL_ACCORDION_NAMES.OWNER}>
      <AccordionItemHeader
        headline={t('owner.label')}
        selectedValueToShow={selectedOwnerName}
      />
      <AccordionItemPanel disableAnimation>
        {!surveyId || !selectedOrgUnits.length ? (
          <Typography>{t('owner.selectSurveyAndOrgUnitFirst')}</Typography>
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                placeholder={t('owner.searchLabel')}
                type="text"
                name="searchOwner"
                onChange={handleSearchChange}
                value={searchString}
                InputProps={{
                  endAdornment: searchString ? (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="clear search"
                        onClick={handleClearSearch}
                        edge="end"
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Box>

            {(isLoading || isFetching) && (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {error && (
              <Typography color="error">{t('error.loadingOwners')}</Typography>
            )}

            {!isLoading &&
              !isFetching &&
              followUpOwners &&
              !followUpOwners.results?.length && (
                <Typography>
                  {searchString ? t('search.noResults') : t('search.noData')}
                </Typography>
              )}
            {surveyId &&
              selectedOrgUnits.length &&
              !isLoading &&
              !isFetching &&
              !!followUpOwners?.results?.length && (
                <RadioGroup
                  aria-label="owner selection"
                  name="owner-selection"
                  value={followUpOwner?.id || ''}
                  onChange={handleOwnerSelect}
                >
                  {followUpOwners.results?.map((owner) => (
                    <FormControlLabel
                      key={owner.id}
                      value={owner.id}
                      control={<Radio />}
                      label={
                        <Typography variant="body1" component="span">
                          {owner.fullName}
                        </Typography>
                      }
                    />
                  ))}
                  {followUpOwners.count >
                    POTENTIAL_FOLLOW_UP_OWNERS_PAGINATION_LIMIT && (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary' }}
                      >
                        {t('owner.moreResultsAvailable', {
                          count:
                            followUpOwners.count -
                            POTENTIAL_FOLLOW_UP_OWNERS_PAGINATION_LIMIT,
                        })}
                      </Typography>
                    </Box>
                  )}
                </RadioGroup>
              )}
          </>
        )}
      </AccordionItemPanel>
    </AccordionItem>
  );
};

export default SelectOwner;
